import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaEye,
  FaPlus,
  FaSave,
  FaSpinner,
} from "react-icons/fa";

import { getPageById, updatePage } from "../../../services/pagesServices";

import PageBlockEditor from "./components/builder/PageBlockEditor";
import PageLivePreview from "./components/builder/PageLivePreview";

import {
  BLOCK_TYPES,
  buildPageHtml,
  extractBuilderBlocks,
  getDefaultPageBlocks,
  newBlock,
  normalizeBlocks,
  stripBuilderMarker,
} from "./components/builder/pageBuilderUtils";

export default function PageContentBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [page, setPage] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [selectedBlockType, setSelectedBlockType] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState("");
  const [isDirty, setIsDirty] = useState(false);

  const previewHtml = useMemo(() => {
    return buildPageHtml(blocks, { includeMarker: false });
  }, [blocks]);

  useEffect(() => {
    let mounted = true;

    const fetchPage = async () => {
      try {
        setLoading(true);
        setMessage("");

        const data = await getPageById(id);

        if (!mounted) return;

        setPage(data || null);

        const savedBlocks = extractBuilderBlocks(data?.content_html || "");

        if (savedBlocks?.length) {
          setBlocks(normalizeBlocks(savedBlocks));
        } else if (data?.content_html) {
          setBlocks([
            {
              id: cryptoId(),
              type: "legacyHtml",
              html: stripBuilderMarker(data.content_html),
            },
          ]);
        } else {
          setBlocks(getDefaultPageBlocks());
        }

        setIsDirty(false);
      } catch (error) {
        console.error("Erreur chargement page:", error);
        setMessage("Impossible de charger la page.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchPage();

    return () => {
      mounted = false;
    };
  }, [id]);

  const updateBlocks = (nextBlocks) => {
    setBlocks(normalizeBlocks(nextBlocks));
    setIsDirty(true);
    setMessage("");
  };

  const addBlock = (type) => {
    if (!type) return;

    updateBlocks([...(blocks || []), newBlock(type)]);
    setSelectedBlockType("");
  };

  const updateBlock = (index, nextBlock) => {
    const next = [...blocks];
    next[index] = nextBlock;
    updateBlocks(next);
  };

  const removeBlock = (index) => {
    updateBlocks(blocks.filter((_, i) => i !== index));
  };

  const duplicateBlock = (index) => {
    const current = blocks[index];
    if (!current) return;

    const copy = {
      ...current,
      id: cryptoId(),
    };

    if (current.type === "cards") {
      copy.items = Array.isArray(current.items)
        ? current.items.map((item) => ({ ...item }))
        : [];
    }

    const next = [...blocks];
    next.splice(index + 1, 0, copy);
    updateBlocks(next);
  };

  const moveBlock = (index, direction) => {
    const targetIndex = index + direction;

    if (targetIndex < 0 || targetIndex >= blocks.length) return;

    const next = [...blocks];
    const current = next[index];

    next[index] = next[targetIndex];
    next[targetIndex] = current;

    updateBlocks(next);
  };

  const handleSave = async () => {
    if (!page?.id) return;

    try {
      setSaving(true);
      setMessage("");

      const content_html = buildPageHtml(blocks, { includeMarker: true });

      const saved = await updatePage(page.id, {
        content_html,
      });

      if (!saved) {
        throw new Error("Sauvegarde impossible.");
      }

      setPage(saved);
      setIsDirty(false);
      setMessage("Contenu enregistré avec succès.");
    } catch (error) {
      console.error("Erreur sauvegarde page:", error);
      setMessage("Erreur lors de l’enregistrement du contenu.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] p-6">
        <div className="mx-auto flex max-w-5xl items-center gap-3 rounded-3xl border border-slate-200 bg-white p-5 text-slate-600 shadow-sm dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
          <FaSpinner className="animate-spin text-sky-600" />
          Chargement de l’éditeur de contenu…
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-[60vh] p-6">
        <div className="mx-auto max-w-5xl rounded-3xl border border-rose-200 bg-rose-50 p-5 text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-200">
          Page introuvable.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 text-slate-950 dark:bg-slate-950 dark:text-slate-50 md:p-6">
      <div className="mx-auto max-w-[1600px]">
        <header className="sticky top-0 z-10 -mx-4 border-b border-slate-200 bg-white/90 px-4 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90 md:-mx-6 md:px-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="mt-1 inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaArrowLeft />
                Retour
              </button>

              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1 text-xs font-medium text-sky-700 dark:bg-sky-950/40 dark:text-sky-200">
                  Éditeur simplifié
                </div>

                <h1 className="mt-2 text-2xl font-bold tracking-tight md:text-3xl">
                  {page.title}
                </h1>

                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                  {page.slug ? <span>/pages/{page.slug}</span> : null}
                  {page.is_active ? (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                      Active
                    </span>
                  ) : (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      Inactive
                    </span>
                  )}
                  {isDirty ? (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                      Modifications non enregistrées
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center xl:justify-end">
              <div className="flex min-w-0 items-center gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <select
                  value={selectedBlockType}
                  onChange={(e) => {
                    setSelectedBlockType(e.target.value);
                    addBlock(e.target.value);
                  }}
                  className="min-w-[210px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus:ring-sky-950"
                >
                  <option value="">+ Ajouter un bloc</option>
                  {BLOCK_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={() => addBlock("paragraph")}
                  className="hidden items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 sm:inline-flex"
                >
                  <FaPlus />
                  Paragraphe
                </button>
              </div>

              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-600 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>

          {message ? (
            <div
              className={`mt-4 rounded-2xl px-4 py-3 text-sm ${
                message.includes("Erreur") || message.includes("Impossible")
                  ? "bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-200"
                  : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200"
              }`}
            >
              {message}
            </div>
          ) : null}
        </header>

        <main className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(420px,0.85fr)]">
          <section className="space-y-4">
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-lg font-bold">Blocs de contenu</h2>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Ajoutez des blocs simples, modifiez le texte, choisissez les
                    images et réorganisez la page.
                  </p>
                </div>

                <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  {blocks.length} bloc{blocks.length > 1 ? "s" : ""}
                </div>
              </div>
            </div>

            {blocks.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-50 text-sky-600 dark:bg-sky-950/40 dark:text-sky-300">
                  <FaPlus />
                </div>

                <h3 className="mt-4 text-lg font-bold">Aucun bloc</h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Ajoutez un premier bloc pour commencer la composition de la
                  page.
                </p>

                <button
                  type="button"
                  onClick={() => addBlock("paragraph")}
                  className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-sky-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-sky-700"
                >
                  <FaPlus />
                  Ajouter un paragraphe
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {blocks.map((block, index) => (
                  <PageBlockEditor
                    key={block.id || `${block.type}-${index}`}
                    block={block}
                    index={index}
                    total={blocks.length}
                    onChange={(nextBlock) => updateBlock(index, nextBlock)}
                    onMoveUp={() => moveBlock(index, -1)}
                    onMoveDown={() => moveBlock(index, 1)}
                    onDuplicate={() => duplicateBlock(index)}
                    onRemove={() => removeBlock(index)}
                  />
                ))}
              </div>
            )}
          </section>

          <aside className="xl:sticky xl:top-[138px] xl:self-start">
            <PageLivePreview
              page={page}
              html={previewHtml}
              onSave={handleSave}
              saving={saving}
            />
          </aside>
        </main>
      </div>
    </div>
  );
}

function cryptoId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}